import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import { ApiError, queryClient } from '@/shared/api'
import { typographyStyle } from '@/shared/lib/catalog'
import { designTokens } from '@/shared/lib/design-tokens'
import { toBrowserMediaUrl } from '@/shared/lib/media-url'
import { Button, Input, Select, Textarea } from '@/shared/ui'
import {
  addAdminBundleItem,
  addAdminCategoryItem,
  createAdminManagedUploadUrl,
  createAdminBundle,
  createAdminCategory,
  createAdminCustomFilter,
  createAdminCustomFilterOption,
  createAdminUserAccessGrant,
  deleteAdminManagedObject,
  deleteAdminBundle,
  deleteAdminBundleItem,
  deleteAdminCategory,
  deleteAdminCategoryItem,
  deleteAdminCustomFilter,
  deleteAdminCustomFilterOption,
  deleteAdminUserAccessEntry,
  deleteAdminUserAccessGrant,
  loadAdminBundleDetail,
  loadAdminBundlesList,
  loadAdminCategoriesList,
  loadAdminCategoryDetail,
  loadAdminCustomFilterDetail,
  loadAdminCustomFiltersList,
  loadAdminUserAccessDetail,
  loadAdminUserAccessList,
  updateAdminBundle,
  updateAdminCategory,
  updateAdminCategoryItemPosition,
  updateAdminCustomFilter,
  updateAdminCustomFilterOption,
  type BundleAvailability,
  type BundleDetail,
  type CatalogItemKind,
  type CategoryDetail,
  type CustomFilterDetail,
  type LinkState,
  type ManagedMediaKind,
  type UserAccessItemKind,
} from '../api/video-library-admin-api'
import {
  formatCatalogCoverValidationHint,
  validateCatalogCoverImage,
} from '../lib/catalog-cover-image'
import { CollectionsTab, ResourcesTab, VideosTab } from './video-library-admin-content-tabs'
import { ReferenceBrowser, TabbedReferenceBrowser } from './reference-browser'

export type AdminVideoTab =
  | 'user-access'
  | 'bundles'
  | 'categories'
  | 'custom-filters'
  | 'resources'
  | 'collections'
  | 'videos'

type WorkspaceProps = {
  activeTab: AdminVideoTab
}

type FieldProps = {
  label: string
  children: ReactNode
  className?: string
}

type BannerTone = 'error' | 'success'
type UploadedManagedFile = {
  bucket: string
  key: string
  browserUrl: string
  fileName: string
  mimeType: string
  sizeBytes: number
}

const pageSize = 12
const storageHttpBase = 'https://storage.yandexcloud.net'
const sectionHeadingStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.heading.h2),
}

const bodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.base),
}

const smallBodyStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.primary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const mutedStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.secondary,
  ...typographyStyle(designTokens.typography.body.sm),
}

const labelStyle = {
  fontFamily: designTokens.typography.fontFamilies.primary,
  color: designTokens.colors.text.strong,
  ...typographyStyle(designTokens.typography.body.baseSemibold),
}

const panelStyle = {
  borderColor: designTokens.colors.border.adminPattern,
  backgroundColor: designTokens.colors.background.surface,
}

const formatDateTime = (value: string) =>
  new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value))

const formatError = (error: unknown) => {
  if (error instanceof ApiError) {
    return `${error.status} ${error.statusText}`
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'Unexpected admin error'
}

const successToneFromMessage = (message: string): BannerTone =>
  message.includes('создан') ||
  message.includes('создана') ||
  message.includes('сохран') ||
  message.includes('удал') ||
  message.includes('загруж')
    ? 'success'
    : 'error'

const resolveSelectedId = <T extends { id: string }>(
  rows: T[] | undefined,
  requestedId: string | null
) => {
  const items = rows ?? []
  if (items.length === 0) {
    return null
  }

  if (requestedId && items.some((item) => item.id === requestedId)) {
    return requestedId
  }

  return items[0]?.id ?? null
}

const toStorageUrl = (bucket: string, key: string) =>
  `${storageHttpBase}/${encodeURIComponent(bucket)}/${key
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')}`

async function uploadManagedFile(input: {
  kind: ManagedMediaKind
  file: File
  entityId: string
}): Promise<UploadedManagedFile> {
  const response = await createAdminManagedUploadUrl({
    kind: input.kind,
    fileName: input.file.name,
    contentType: input.file.type || 'application/octet-stream',
    entityId: input.entityId,
  })

  const uploadResponse = await fetch(response.upload.uploadUrl, {
    method: response.upload.uploadMethod,
    headers: response.upload.requiredHeaders,
    body: input.file,
  })

  if (!uploadResponse.ok) {
    throw new Error(`Upload failed: ${uploadResponse.status}`)
  }

  return {
    bucket: response.upload.bucket,
    key: response.upload.key,
    browserUrl: toStorageUrl(response.upload.bucket, response.upload.key),
    fileName: input.file.name,
    mimeType: input.file.type || 'application/octet-stream',
    sizeBytes: input.file.size,
  }
}

async function deleteManagedFileIfPresent(key: string | null | undefined, kind: ManagedMediaKind) {
  if (!key) {
    return
  }

  await deleteAdminManagedObject({ key, kind })
}

function FeedbackBanner({ message, tone = 'error' }: { message: string; tone?: BannerTone }) {
  return (
    <div
      className="rounded-2xl px-4 py-3"
      style={{
        ...smallBodyStyle,
        backgroundColor: tone === 'success' ? '#dcfce7' : '#fee2e2',
        color: tone === 'success' ? '#166534' : '#991b1b',
      }}
    >
      {message}
    </div>
  )
}

function PanelSection({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-[24px] border p-4" style={panelStyle}>
      <div className="mb-4 flex flex-col gap-1">
        <h3 style={sectionHeadingStyle}>{title}</h3>
        {description ? <p style={mutedStyle}>{description}</p> : null}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  )
}

function Field({ label, children, className }: FieldProps) {
  return (
    <div className={className ? `flex flex-col gap-2 ${className}` : 'flex flex-col gap-2'}>
      <span style={labelStyle}>{label}</span>
      {children}
    </div>
  )
}

function ManagedFileField({
  label,
  fileLabel,
  accept,
  hint,
  showInlineFeedback = false,
  uploadSuccessMessage = 'Файл загружен',
  removeSuccessMessage = 'Файл удалён',
  onUpload,
  onRemove,
}: {
  label: string
  fileLabel: string | null
  accept?: string
  hint?: string
  showInlineFeedback?: boolean
  uploadSuccessMessage?: string
  removeSuccessMessage?: string
  onUpload: (file: File) => Promise<void>
  onRemove: () => Promise<void>
}) {
  const [inlineFeedback, setInlineFeedback] = useState<string | null>(null)

  const uploadFile = async (file: File) => {
    if (showInlineFeedback) {
      setInlineFeedback(null)
    }

    try {
      await onUpload(file)

      if (showInlineFeedback) {
        setInlineFeedback(uploadSuccessMessage)
      }
    } catch (error) {
      if (showInlineFeedback) {
        setInlineFeedback(formatError(error))
        return
      }

      throw error
    }
  }

  const removeFile = async () => {
    if (showInlineFeedback) {
      setInlineFeedback(null)
    }

    try {
      await onRemove()

      if (showInlineFeedback) {
        setInlineFeedback(removeSuccessMessage)
      }
    } catch (error) {
      if (showInlineFeedback) {
        setInlineFeedback(formatError(error))
        return
      }

      throw error
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border p-4" style={panelStyle}>
      <span style={labelStyle}>{label}</span>
      <span style={fileLabel ? bodyStyle : mutedStyle}>
        {fileLabel ? `Текущий файл: ${fileLabel}` : 'Файл не привязан'}
      </span>
      <input
        type="file"
        accept={accept}
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) {
            void uploadFile(file)
          }
          event.currentTarget.value = ''
        }}
      />
      <span style={mutedStyle}>
        После выбора новый файл загружается сразу. Нативное поле затем очищается автоматически.
      </span>
      {hint ? <span style={mutedStyle}>{hint}</span> : null}
      {inlineFeedback ? (
        <FeedbackBanner message={inlineFeedback} tone={successToneFromMessage(inlineFeedback)} />
      ) : null}
      {fileLabel ? (
        <div className="flex justify-end">
          <Button type="button" variant="secondaryAssist" onClick={() => void removeFile()}>
            Удалить файл
          </Button>
        </div>
      ) : null}
    </div>
  )
}

function PaginationControls({
  page,
  hasMore,
  total,
  limit,
  onPrevious,
  onNext,
  onSelectPage,
}: {
  page: number
  hasMore: boolean
  total: number
  limit: number
  onPrevious: () => void
  onNext: () => void
  onSelectPage: (page: number) => void
}) {
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const pageNumbers = new Set<number>([1, totalPages, page - 1, page, page + 1, page + 2])
  const visiblePages = Array.from(pageNumbers)
    .filter((item) => item >= 1 && item <= totalPages)
    .sort((left, right) => left - right)

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span style={mutedStyle}>Всего: {total}</span>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="secondaryAssist" disabled={page <= 1} onClick={onPrevious}>
          Назад
        </Button>
        {visiblePages.map((item, index) => {
          const previousPage = visiblePages[index - 1]
          const showEllipsis = previousPage !== undefined && item - previousPage > 1

          return (
            <div key={item} className="flex items-center gap-2">
              {showEllipsis ? <span style={mutedStyle}>…</span> : null}
              <Button
                type="button"
                variant={item === page ? 'primary' : 'secondaryAssist'}
                onClick={() => onSelectPage(item)}
              >
                {item}
              </Button>
            </div>
          )
        })}
        <Button type="button" variant="secondaryAssist" disabled={!hasMore} onClick={onNext}>
          Вперёд
        </Button>
      </div>
    </div>
  )
}

function ListSelectionButton({
  active,
  onClick,
  coverUrl,
  coverAlt,
  children,
}: {
  active: boolean
  onClick: () => void
  coverUrl?: string | null
  coverAlt?: string
  children: ReactNode
}) {
  const normalizedCoverUrl = toBrowserMediaUrl(coverUrl)

  return (
    <button
      type="button"
      aria-pressed={active}
      className="rounded-2xl border px-4 py-4 text-left transition"
      style={{
        ...panelStyle,
        borderColor: active
          ? designTokens.colors.border.inputFocus
          : designTokens.colors.border.adminPattern,
        backgroundColor: active ? '#f8f4ff' : designTokens.colors.background.surface,
        boxShadow: active
          ? '0 0 0 2px rgba(118, 92, 255, 0.12), 0 18px 36px rgba(15, 23, 42, 0.08)'
          : '0 8px 24px rgba(15, 23, 42, 0.04)',
      }}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        <div
          className="shrink-0 rounded-full"
          style={{
            width: active ? '10px' : '8px',
            minHeight: active ? '56px' : '48px',
            backgroundColor: active ? designTokens.colors.border.inputFocus : '#ddd3eb',
          }}
        />
        {normalizedCoverUrl ? (
          <img
            src={normalizedCoverUrl}
            alt={coverAlt ?? 'Cover preview'}
            className="h-16 w-24 shrink-0 rounded-2xl object-cover"
          />
        ) : null}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </button>
  )
}

function UserAccessTab() {
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [linkState, setLinkState] = useState<LinkState>('all')
  const [page, setPage] = useState(1)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [grantKind, setGrantKind] = useState<UserAccessItemKind>('bundle')
  const [feedback, setFeedback] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin-video-library', 'user-access', page, appliedSearch, linkState],
    queryFn: () =>
      loadAdminUserAccessList({
        page,
        limit: pageSize,
        search: appliedSearch,
        linkState,
      }),
  })

  const selectedId = resolveSelectedId(listQuery.data?.data, requestedId)

  const detailQuery = useQuery({
    queryKey: ['admin-video-library', 'user-access-detail', selectedId],
    queryFn: () => loadAdminUserAccessDetail(selectedId as string),
    enabled: Boolean(selectedId),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-video-library', 'user-access'] })

    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: ['admin-video-library', 'user-access-detail', selectedId],
      })
    }
  }

  const deleteEntry = async () => {
    if (!selectedId || !detailQuery.data?.item.canDelete) {
      return
    }

    if (!window.confirm('Удалить этот users_catalog и все его access-связи?')) {
      return
    }

    try {
      await deleteAdminUserAccessEntry(selectedId)
      setRequestedId(null)
      setFeedback('users_catalog удалён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const removeGrant = async (accessId: string) => {
    if (!selectedId || !window.confirm('Удалить этот доступ?')) {
      return
    }

    try {
      await deleteAdminUserAccessGrant({ id: selectedId, accessId })
      setFeedback('Доступ удалён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const addGrant = async (referenceId: string) => {
    if (!selectedId) {
      return
    }

    await createAdminUserAccessGrant({
      id: selectedId,
      itemKind: grantKind,
      itemId: referenceId,
    })

    await refresh()
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <PanelSection
        title="Список users_catalog"
        description="Email, связь с users и суммарные доступы."
      >
        <div className="grid gap-3">
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Поиск по email / name"
          />
          <Select
            value={linkState}
            onChange={(event) => setLinkState(event.target.value as LinkState)}
          >
            <option value="all">Все связи</option>
            <option value="linked">Только linked</option>
            <option value="unlinked">Только unlinked</option>
          </Select>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setAppliedSearch(draftSearch.trim())
              }}
            >
              Применить
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setDraftSearch('')
                setAppliedSearch('')
                setLinkState('all')
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>

        {listQuery.isLoading ? <p style={mutedStyle}>Загружаю список…</p> : null}
        {(listQuery.data?.data ?? []).map((item) => (
          <ListSelectionButton
            key={item.id}
            active={selectedId === item.id}
            onClick={() => setRequestedId(item.id)}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.email ?? 'Без email'}</strong>
              <span style={mutedStyle}>
                {item.userId ? `Linked: ${item.linkedUserEmail ?? item.userId}` : 'Not linked'}
              </span>
              <span style={mutedStyle}>Access rows: {item.accessSummary.total}</span>
            </div>
          </ListSelectionButton>
        ))}

        {listQuery.data ? (
          <PaginationControls
            page={page}
            hasMore={listQuery.data.meta.hasMore}
            total={listQuery.data.meta.total}
            limit={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
            onSelectPage={setPage}
          />
        ) : null}
      </PanelSection>

      <div className="flex flex-col gap-6">
        {feedback ? (
          <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
        ) : null}

        {detailQuery.data ? (
          <>
            <PanelSection title="Карточка users_catalog">
              <div className="grid gap-3 lg:grid-cols-2">
                <div>
                  <span style={labelStyle}>Email</span>
                  <p style={bodyStyle}>{detailQuery.data.item.email ?? 'Нет значения'}</p>
                </div>
                <div>
                  <span style={labelStyle}>Linked user</span>
                  <p style={bodyStyle}>
                    {detailQuery.data.item.userId
                      ? (detailQuery.data.item.linkedUserEmail ?? detailQuery.data.item.userId)
                      : 'Нет связи'}
                  </p>
                </div>
                <div>
                  <span style={labelStyle}>Uscreen user id</span>
                  <p style={bodyStyle}>{detailQuery.data.item.uscreenUserId}</p>
                </div>
                <div>
                  <span style={labelStyle}>Обновлён</span>
                  <p style={bodyStyle}>{formatDateTime(detailQuery.data.item.updatedAt)}</p>
                </div>
              </div>

              {detailQuery.data.item.canDelete ? (
                <div className="flex justify-end">
                  <Button
                    type="button"
                    variant="secondaryAssist"
                    onClick={() => void deleteEntry()}
                  >
                    Удалить users_catalog
                  </Button>
                </div>
              ) : (
                <p style={mutedStyle}>Этот users_catalog связан с users и не может быть удалён.</p>
              )}
            </PanelSection>

            <PanelSection title="Доступы">
              <div className="flex flex-col gap-3">
                {detailQuery.data.access.map((access) => (
                  <div
                    key={access.id}
                    className="flex flex-col gap-3 rounded-2xl border p-4 lg:flex-row lg:items-center lg:justify-between"
                    style={panelStyle}
                  >
                    <div className="flex flex-col gap-1">
                      <strong style={smallBodyStyle}>{access.title}</strong>
                      <span style={mutedStyle}>
                        {access.itemKind} · {access.urlSlug ?? 'Без slug'} · {access.source}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="secondaryAssist"
                      onClick={() => void removeGrant(access.id)}
                    >
                      Удалить доступ
                    </Button>
                  </div>
                ))}

                {detailQuery.data.access.length === 0 ? (
                  <p style={mutedStyle}>Пока нет выданных доступов.</p>
                ) : null}
              </div>
            </PanelSection>

            <PanelSection title="Добавить доступ">
              <TabbedReferenceBrowser
                activeId={grantKind}
                ariaLabel="Тип выдаваемого доступа"
                onChange={setGrantKind}
                tabs={[
                  {
                    id: 'bundle',
                    label: 'Bundles',
                    browser: {
                      kind: 'bundle',
                      actionLabel: 'Добавить доступ',
                      linkedItems: detailQuery.data.access
                        .filter((access) => access.itemKind === 'bundle' && access.itemId)
                        .map((access) => ({
                          itemId: access.itemId as string,
                          linkId: access.id,
                        })),
                      onAdd: addGrant,
                      onRemove: (_itemId, linkId) =>
                        linkId ? removeGrant(linkId) : Promise.resolve(),
                    },
                  },
                  {
                    id: 'collection',
                    label: 'Collections',
                    browser: {
                      kind: 'collection',
                      actionLabel: 'Добавить доступ',
                      linkedItems: detailQuery.data.access
                        .filter((access) => access.itemKind === 'collection' && access.itemId)
                        .map((access) => ({
                          itemId: access.itemId as string,
                          linkId: access.id,
                        })),
                      onAdd: addGrant,
                      onRemove: (_itemId, linkId) =>
                        linkId ? removeGrant(linkId) : Promise.resolve(),
                    },
                  },
                  {
                    id: 'video',
                    label: 'Videos',
                    browser: {
                      kind: 'video',
                      actionLabel: 'Добавить доступ',
                      linkedItems: detailQuery.data.access
                        .filter((access) => access.itemKind === 'video' && access.itemId)
                        .map((access) => ({
                          itemId: access.itemId as string,
                          linkId: access.id,
                        })),
                      onAdd: addGrant,
                      onRemove: (_itemId, linkId) =>
                        linkId ? removeGrant(linkId) : Promise.resolve(),
                    },
                  },
                ]}
              />
            </PanelSection>
          </>
        ) : (
          <PanelSection title="Карточка users_catalog">
            <p style={mutedStyle}>Выберите запись слева.</p>
          </PanelSection>
        )}
      </div>
    </div>
  )
}

function BundleEditor({
  detail,
  onSave,
  onDelete,
  onUploadImage,
  onRemoveImage,
  onAddItem,
  onRemoveItem,
}: {
  detail: BundleDetail
  onSave: (input: {
    title: string
    tariffName: string | null
    mainBundle: boolean
    displayOnCatalog: boolean
    descriptionHtml: string | null
    availability: BundleAvailability
    priceCents: number
    priceCurrency: string
    rentalDuration: string | null
  }) => Promise<void>
  onDelete: () => Promise<void>
  onUploadImage: (file: File) => Promise<UploadedManagedFile>
  onRemoveImage: () => Promise<void>
  onAddItem: (itemKind: CatalogItemKind, referenceId: string) => Promise<void>
  onRemoveItem: (itemKind: CatalogItemKind, linkId: string) => Promise<void>
}) {
  const relationPageSize = 10
  const normalizedBundleImageUrl = toBrowserMediaUrl(detail.bundle.imageUrl)
  const [title, setTitle] = useState(detail.bundle.title)
  const [tariffName, setTariffName] = useState(detail.bundle.tariffName ?? '')
  const [mainBundle, setMainBundle] = useState(detail.bundle.mainBundle)
  const [displayOnCatalog, setDisplayOnCatalog] = useState(detail.bundle.displayOnCatalog)
  const [descriptionHtml, setDescriptionHtml] = useState(detail.bundle.descriptionHtml ?? '')
  const [availability, setAvailability] = useState<BundleAvailability>(detail.bundle.availability)
  const [priceCents, setPriceCents] = useState(String(detail.bundle.priceCents))
  const [priceCurrency, setPriceCurrency] = useState(detail.bundle.priceCurrency)
  const [rentalDuration, setRentalDuration] = useState(detail.bundle.rentalDuration ?? '')
  const [collectionsPage, setCollectionsPage] = useState(1)
  const [videosPage, setVideosPage] = useState(1)

  const handleUploadImage = async (file: File) => {
    await onUploadImage(file)
  }

  const handleRemoveImage = async () => {
    await onRemoveImage()
  }

  const visibleCollections = detail.collections.slice(
    (collectionsPage - 1) * relationPageSize,
    collectionsPage * relationPageSize
  )
  const visibleVideos = detail.videos.slice(
    (videosPage - 1) * relationPageSize,
    videosPage * relationPageSize
  )
  const hasMoreCollections = collectionsPage * relationPageSize < detail.collections.length
  const hasMoreVideos = videosPage * relationPageSize < detail.videos.length

  return (
    <>
      <PanelSection title="Параметры бандла">
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="Availability">
            <Select
              value={availability}
              onChange={(event) => setAvailability(event.target.value as BundleAvailability)}
            >
              <option value="private">private</option>
              <option value="public">public</option>
            </Select>
          </Field>
          <Field label="Tariff name">
            <Input value={tariffName} onChange={(event) => setTariffName(event.target.value)} />
          </Field>
          <Field label="Main bundle">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={mainBundle}
                onChange={(event) => setMainBundle(event.target.checked)}
              />
              <span style={bodyStyle}>Использовать как основной тарифный бандл</span>
            </label>
          </Field>
          <Field label="Catalog">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={displayOnCatalog}
                onChange={(event) => setDisplayOnCatalog(event.target.checked)}
              />
              <span style={bodyStyle}>Показывать бандл в каталоге</span>
            </label>
          </Field>
          <Field label="Description" className="lg:col-span-2">
            <Textarea
              value={descriptionHtml}
              onChange={(event) => setDescriptionHtml(event.target.value)}
            />
          </Field>
          <Field label="Price cents">
            <Input
              type="number"
              value={priceCents}
              onChange={(event) => setPriceCents(event.target.value)}
            />
          </Field>
          <Field label="Currency">
            <Input
              value={priceCurrency}
              onChange={(event) => setPriceCurrency(event.target.value)}
            />
          </Field>
          <Field label="Rental duration">
            <Input
              value={rentalDuration}
              onChange={(event) => setRentalDuration(event.target.value)}
            />
          </Field>
          <Field label="Updated">
            <p style={bodyStyle}>{formatDateTime(detail.bundle.updatedAt)}</p>
          </Field>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          {detail.bundle.canDelete ? (
            <Button type="button" variant="secondaryAssist" onClick={() => void onDelete()}>
              Удалить бандл
            </Button>
          ) : null}
          <Button
            type="button"
            onClick={() =>
              void onSave({
                title: title.trim(),
                tariffName: tariffName.trim() || null,
                mainBundle,
                displayOnCatalog,
                descriptionHtml: descriptionHtml.trim() || null,
                availability,
                priceCents: Number(priceCents || '0'),
                priceCurrency: priceCurrency.trim() || 'RUB',
                rentalDuration: rentalDuration.trim() || null,
              })
            }
          >
            Сохранить бандл
          </Button>
        </div>
      </PanelSection>

      <PanelSection
        title="Cover"
        description="Обложка бандла загружается, заменяется и удаляется сразу, отдельно от основной кнопки сохранения."
      >
        <ManagedFileField
          label="Cover"
          fileLabel={detail.bundle.imageKey || detail.bundle.imageUrl}
          accept="image/jpeg,image/png,image/webp"
          hint={formatCatalogCoverValidationHint()}
          showInlineFeedback
          uploadSuccessMessage="Обложка бандла загружена"
          removeSuccessMessage="Обложка бандла удалена"
          onUpload={handleUploadImage}
          onRemove={handleRemoveImage}
        />
        {normalizedBundleImageUrl ? (
          <img
            src={normalizedBundleImageUrl}
            alt={title}
            className="max-h-52 rounded-2xl object-cover"
          />
        ) : null}
      </PanelSection>

      <PanelSection title="Коллекции в бандле">
        {visibleCollections.map((item) => (
          <div
            key={item.linkId}
            className="flex items-center justify-between gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>position: {item.position}</span>
            </div>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => void onRemoveItem('collection', item.linkId)}
            >
              Удалить связь
            </Button>
          </div>
        ))}
        {detail.collections.length > relationPageSize ? (
          <PaginationControls
            page={collectionsPage}
            hasMore={hasMoreCollections}
            total={detail.collections.length}
            limit={relationPageSize}
            onPrevious={() => setCollectionsPage((current) => Math.max(1, current - 1))}
            onNext={() =>
              setCollectionsPage((current) => (hasMoreCollections ? current + 1 : current))
            }
            onSelectPage={setCollectionsPage}
          />
        ) : null}
        <ReferenceBrowser
          kind="collection"
          actionLabel="Добавить коллекцию"
          linkedItems={detail.collections.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={(referenceId) => onAddItem('collection', referenceId)}
          onRemove={(_itemId, linkId) =>
            linkId ? onRemoveItem('collection', linkId) : Promise.resolve()
          }
        />
      </PanelSection>

      <PanelSection title="Видео в бандле">
        {visibleVideos.map((item) => (
          <div
            key={item.linkId}
            className="flex items-center justify-between gap-3 rounded-2xl border p-4"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>position: {item.position}</span>
            </div>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => void onRemoveItem('video', item.linkId)}
            >
              Удалить связь
            </Button>
          </div>
        ))}
        {detail.videos.length > relationPageSize ? (
          <PaginationControls
            page={videosPage}
            hasMore={hasMoreVideos}
            total={detail.videos.length}
            limit={relationPageSize}
            onPrevious={() => setVideosPage((current) => Math.max(1, current - 1))}
            onNext={() => setVideosPage((current) => (hasMoreVideos ? current + 1 : current))}
            onSelectPage={setVideosPage}
          />
        ) : null}
        <ReferenceBrowser
          kind="video"
          actionLabel="Добавить видео"
          linkedItems={detail.videos.map((item) => ({
            itemId: item.itemId,
            linkId: item.linkId,
          }))}
          onAdd={(referenceId) => onAddItem('video', referenceId)}
          onRemove={(_itemId, linkId) =>
            linkId ? onRemoveItem('video', linkId) : Promise.resolve()
          }
        />
      </PanelSection>
    </>
  )
}

function BundlesTab() {
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [draftMainBundle, setDraftMainBundle] = useState<'all' | 'true' | 'false'>('all')
  const [appliedMainBundle, setAppliedMainBundle] = useState<'all' | 'true' | 'false'>('all')
  const [page, setPage] = useState(1)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [createTitle, setCreateTitle] = useState('')
  const [createTariffName, setCreateTariffName] = useState('')
  const [createMainBundle, setCreateMainBundle] = useState(false)
  const [createDisplayOnCatalog, setCreateDisplayOnCatalog] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin-video-library', 'bundles', page, appliedSearch, appliedMainBundle],
    queryFn: () =>
      loadAdminBundlesList({
        page,
        limit: pageSize,
        search: appliedSearch,
        mainBundle: appliedMainBundle,
      }),
  })

  const selectedId = resolveSelectedId(listQuery.data?.data, requestedId)

  const detailQuery = useQuery({
    queryKey: ['admin-video-library', 'bundle-detail', selectedId],
    queryFn: () => loadAdminBundleDetail(selectedId as string),
    enabled: Boolean(selectedId),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-video-library', 'bundles'] })

    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: ['admin-video-library', 'bundle-detail', selectedId],
      })
    }
  }

  const createBundleHandler = async () => {
    if (!createTitle.trim()) {
      return
    }

    try {
      const response = await createAdminBundle({
        title: createTitle.trim(),
        tariffName: createTariffName.trim() || null,
        mainBundle: createMainBundle,
        displayOnCatalog: createDisplayOnCatalog,
      })
      setCreateTitle('')
      setCreateTariffName('')
      setCreateMainBundle(false)
      setCreateDisplayOnCatalog(false)
      setRequestedId(response.detail.bundle.id)
      setFeedback('Бандл создан')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const saveBundle = async (input: Parameters<typeof updateAdminBundle>[1]) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminBundle(selectedId, input)
      setFeedback('Бандл сохранён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const uploadImage = async (file: File) => {
    if (!selectedId) {
      throw new Error('Bundle is not selected')
    }

    let upload: UploadedManagedFile | null = null

    try {
      await validateCatalogCoverImage(file)
      const previousKey = detailQuery.data?.bundle.imageKey
      upload = await uploadManagedFile({ kind: 'bundleCover', file, entityId: selectedId })
      await updateAdminBundle(selectedId, {
        imageBucket: upload.bucket,
        imageKey: upload.key,
        imageUrl: upload.browserUrl,
      })
      if (previousKey && previousKey !== upload.key) {
        await deleteManagedFileIfPresent(previousKey, 'bundleCover')
      }
      await refresh()
      return upload
    } catch (error) {
      if (upload?.key) {
        await deleteManagedFileIfPresent(upload.key, 'bundleCover').catch(() => undefined)
      }
      throw error
    }
  }

  const removeImage = async () => {
    if (!selectedId || !detailQuery.data) {
      return
    }

    if (!window.confirm('Удалить обложку бандла?')) {
      return
    }

    const oldKey = detailQuery.data.bundle.imageKey

    await updateAdminBundle(selectedId, {
      imageBucket: null,
      imageKey: null,
      imageUrl: null,
    })
    await deleteManagedFileIfPresent(oldKey, 'bundleCover')
    await refresh()
  }

  const addItem = async (itemKind: CatalogItemKind, referenceId: string) => {
    if (!selectedId) {
      return
    }

    await addAdminBundleItem({
      id: selectedId,
      itemKind,
      itemId: referenceId,
    })

    await refresh()
  }

  const removeItem = async (itemKind: CatalogItemKind, linkId: string) => {
    if (!selectedId || !window.confirm('Удалить связь из бандла?')) {
      return
    }

    try {
      await deleteAdminBundleItem({ id: selectedId, itemKind, linkId })
      setFeedback('Связь удалена')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const deleteBundleHandler = async () => {
    if (!selectedId || !detailQuery.data?.bundle.canDelete) {
      return
    }

    if (!window.confirm('Удалить бандл?')) {
      return
    }

    try {
      await deleteAdminBundle(selectedId)
      setRequestedId(null)
      setFeedback('Бандл удалён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <PanelSection title="Бандлы" description="Создание, список и базовые атрибуты.">
        <div className="grid gap-3">
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Поиск по title"
          />
          <Select
            value={draftMainBundle}
            onChange={(event) => setDraftMainBundle(event.target.value as 'all' | 'true' | 'false')}
          >
            <option value="all">Main bundle: all</option>
            <option value="true">Main bundle: true</option>
            <option value="false">Main bundle: false</option>
          </Select>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setAppliedSearch(draftSearch.trim())
                setAppliedMainBundle(draftMainBundle)
              }}
            >
              Применить
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setDraftSearch('')
                setAppliedSearch('')
                setDraftMainBundle('all')
                setAppliedMainBundle('all')
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>

        <div className="grid gap-2 rounded-2xl border p-4" style={panelStyle}>
          <span style={labelStyle}>Новый бандл</span>
          <Input
            value={createTitle}
            onChange={(event) => setCreateTitle(event.target.value)}
            placeholder="Bundle title"
          />
          <Input
            value={createTariffName}
            onChange={(event) => setCreateTariffName(event.target.value)}
            placeholder="Tariff name"
          />
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={createMainBundle}
              onChange={(event) => setCreateMainBundle(event.target.checked)}
            />
            <span style={bodyStyle}>Создать как основной тарифный бандл</span>
          </label>
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={createDisplayOnCatalog}
              onChange={(event) => setCreateDisplayOnCatalog(event.target.checked)}
            />
            <span style={bodyStyle}>Показывать в каталоге</span>
          </label>
          <Button type="button" onClick={() => void createBundleHandler()}>
            Создать
          </Button>
        </div>

        {(listQuery.data?.data ?? []).map((item) => (
          <ListSelectionButton
            key={item.id}
            active={selectedId === item.id}
            onClick={() => setRequestedId(item.id)}
            coverUrl={item.imageUrl}
            coverAlt={item.title}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                collections: {item.collectionCount} · videos: {item.videoCount}
              </span>
              <span style={mutedStyle}>
                {item.availability} · {item.priceCents} {item.priceCurrency}
              </span>
              <span style={mutedStyle}>
                tariff: {item.tariffName ?? 'not set'} {item.mainBundle ? '· main bundle' : ''}
                {item.displayOnCatalog ? ' · catalog' : ' · hidden from catalog'}
              </span>
            </div>
          </ListSelectionButton>
        ))}

        {listQuery.data ? (
          <PaginationControls
            page={page}
            hasMore={listQuery.data.meta.hasMore}
            total={listQuery.data.meta.total}
            limit={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
            onSelectPage={setPage}
          />
        ) : null}
      </PanelSection>

      <div className="flex flex-col gap-6">
        {feedback ? (
          <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
        ) : null}

        {detailQuery.data ? (
          <BundleEditor
            key={detailQuery.data.bundle.id}
            detail={detailQuery.data}
            onSave={saveBundle}
            onDelete={deleteBundleHandler}
            onUploadImage={uploadImage}
            onRemoveImage={removeImage}
            onAddItem={addItem}
            onRemoveItem={removeItem}
          />
        ) : (
          <PanelSection title="Параметры бандла">
            <p style={mutedStyle}>Выберите бандл слева.</p>
          </PanelSection>
        )}
      </div>
    </div>
  )
}

function CategoryEditor({
  detail,
  onSave,
  onDelete,
  onAddItem,
  onMoveItem,
  onRemoveItem,
}: {
  detail: CategoryDetail
  onSave: (input: {
    title: string
    urlSlug: string
    color: string | null
    position: number
  }) => Promise<void>
  onDelete: () => Promise<void>
  onAddItem: (itemKind: CatalogItemKind, referenceId: string) => Promise<void>
  onMoveItem: (linkId: string, nextPosition: number) => Promise<void>
  onRemoveItem: (itemKind: CatalogItemKind, linkId: string) => Promise<void>
}) {
  const [title, setTitle] = useState(detail.category.title)
  const [urlSlug, setUrlSlug] = useState(detail.category.urlSlug)
  const [color, setColor] = useState(detail.category.color ?? '')
  const [position, setPosition] = useState(String(detail.category.position))
  const [newItemKind, setNewItemKind] = useState<CatalogItemKind>('collection')

  return (
    <>
      <PanelSection
        title="Параметры категории"
        description="В текущей SQL-схеме у категории нет отдельного description поля, поэтому здесь редактируются только существующие columns."
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="URL slug">
            <Input value={urlSlug} onChange={(event) => setUrlSlug(event.target.value)} />
          </Field>
          <Field label="Color">
            <Input value={color} onChange={(event) => setColor(event.target.value)} />
          </Field>
          <Field label="Position">
            <Input
              type="number"
              value={position}
              onChange={(event) => setPosition(event.target.value)}
            />
          </Field>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondaryAssist" onClick={() => void onDelete()}>
            Удалить категорию
          </Button>
          <Button
            type="button"
            onClick={() =>
              void onSave({
                title: title.trim(),
                urlSlug: urlSlug.trim(),
                color: color.trim() || null,
                position: Number(position || '0'),
              })
            }
          >
            Сохранить категорию
          </Button>
        </div>
      </PanelSection>

      <PanelSection title="Элементы категории">
        {detail.items.map((item, index) => (
          <div
            key={item.linkId}
            className="flex flex-col gap-3 rounded-2xl border p-4 lg:flex-row lg:items-center lg:justify-between"
            style={panelStyle}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title ?? 'Missing item'}</strong>
              <span style={mutedStyle}>
                {item.itemKind} · position: {item.position} · {item.urlSlug ?? 'Без slug'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === 0}
                onClick={() => void onMoveItem(item.linkId, item.position - 1)}
              >
                Вверх
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === detail.items.length - 1}
                onClick={() => void onMoveItem(item.linkId, item.position + 1)}
              >
                Вниз
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => void onRemoveItem(item.itemKind, item.linkId)}
              >
                Удалить
              </Button>
            </div>
          </div>
        ))}

        <div className="grid gap-3 rounded-2xl border p-4" style={panelStyle}>
          <TabbedReferenceBrowser
            activeId={newItemKind}
            ariaLabel="Тип элемента для категории"
            onChange={setNewItemKind}
            tabs={[
              {
                id: 'collection',
                label: 'Collections',
                browser: {
                  kind: 'collection',
                  actionLabel: 'Добавить в категорию',
                  linkedItems: detail.items
                    .filter((item) => item.itemKind === 'collection' && item.itemId)
                    .map((item) => ({
                      itemId: item.itemId as string,
                      linkId: item.linkId,
                    })),
                  onAdd: (referenceId) => onAddItem('collection', referenceId),
                  onRemove: (_itemId, linkId) =>
                    linkId ? onRemoveItem('collection', linkId) : Promise.resolve(),
                },
              },
              {
                id: 'video',
                label: 'Videos',
                browser: {
                  kind: 'video',
                  actionLabel: 'Добавить в категорию',
                  linkedItems: detail.items
                    .filter((item) => item.itemKind === 'video' && item.itemId)
                    .map((item) => ({
                      itemId: item.itemId as string,
                      linkId: item.linkId,
                    })),
                  onAdd: (referenceId) => onAddItem('video', referenceId),
                  onRemove: (_itemId, linkId) =>
                    linkId ? onRemoveItem('video', linkId) : Promise.resolve(),
                },
              },
            ]}
          />
        </div>
      </PanelSection>
    </>
  )
}

function CategoriesTab() {
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [createTitle, setCreateTitle] = useState('')
  const [createSlug, setCreateSlug] = useState('')
  const [createColor, setCreateColor] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin-video-library', 'categories', page, appliedSearch],
    queryFn: () => loadAdminCategoriesList({ page, limit: pageSize, search: appliedSearch }),
  })

  const selectedId = resolveSelectedId(listQuery.data?.data, requestedId)

  const detailQuery = useQuery({
    queryKey: ['admin-video-library', 'category-detail', selectedId],
    queryFn: () => loadAdminCategoryDetail(selectedId as string),
    enabled: Boolean(selectedId),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-video-library', 'categories'] })

    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: ['admin-video-library', 'category-detail', selectedId],
      })
    }
  }

  const createCategoryHandler = async () => {
    if (!createTitle.trim() || !createSlug.trim()) {
      return
    }

    try {
      const response = await createAdminCategory({
        title: createTitle.trim(),
        urlSlug: createSlug.trim(),
        color: createColor.trim() || null,
      })
      setCreateTitle('')
      setCreateSlug('')
      setCreateColor('')
      setRequestedId(response.detail.category.id)
      setFeedback('Категория создана')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const saveCategory = async (input: Parameters<typeof updateAdminCategory>[1]) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminCategory(selectedId, input)
      setFeedback('Категория сохранена')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const addItem = async (itemKind: CatalogItemKind, referenceId: string) => {
    if (!selectedId) {
      return
    }

    await addAdminCategoryItem({
      id: selectedId,
      itemKind,
      itemId: referenceId,
    })

    await refresh()
  }

  const moveItem = async (linkId: string, nextPosition: number) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminCategoryItemPosition({
        id: selectedId,
        linkId,
        position: nextPosition,
      })
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const removeItem = async (itemKind: CatalogItemKind, linkId: string) => {
    if (!selectedId || !window.confirm('Удалить элемент из категории?')) {
      return
    }

    try {
      await deleteAdminCategoryItem({ id: selectedId, itemKind, linkId })
      setFeedback('Элемент удалён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const deleteCategoryHandler = async () => {
    if (!selectedId || !window.confirm('Удалить категорию?')) {
      return
    }

    try {
      await deleteAdminCategory(selectedId)
      setRequestedId(null)
      setFeedback('Категория удалена')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <PanelSection
        title="Категории"
        description="Управление текущими stored fields и mixed list items."
      >
        <div className="grid gap-3">
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Поиск по title / slug"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setAppliedSearch(draftSearch.trim())
              }}
            >
              Применить
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setDraftSearch('')
                setAppliedSearch('')
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>

        <div className="grid gap-2 rounded-2xl border p-4" style={panelStyle}>
          <span style={labelStyle}>Новая категория</span>
          <Input
            value={createTitle}
            onChange={(event) => setCreateTitle(event.target.value)}
            placeholder="Title"
          />
          <Input
            value={createSlug}
            onChange={(event) => setCreateSlug(event.target.value)}
            placeholder="URL slug"
          />
          <Input
            value={createColor}
            onChange={(event) => setCreateColor(event.target.value)}
            placeholder="Color (optional)"
          />
          <Button type="button" onClick={() => void createCategoryHandler()}>
            Создать
          </Button>
        </div>

        {(listQuery.data?.data ?? []).map((item) => (
          <ListSelectionButton
            key={item.id}
            active={selectedId === item.id}
            onClick={() => setRequestedId(item.id)}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>slug: {item.urlSlug}</span>
              <span style={mutedStyle}>
                position: {item.position} · items: {item.itemCount}
              </span>
            </div>
          </ListSelectionButton>
        ))}

        {listQuery.data ? (
          <PaginationControls
            page={page}
            hasMore={listQuery.data.meta.hasMore}
            total={listQuery.data.meta.total}
            limit={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
            onSelectPage={setPage}
          />
        ) : null}
      </PanelSection>

      <div className="flex flex-col gap-6">
        {feedback ? (
          <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
        ) : null}

        {detailQuery.data ? (
          <CategoryEditor
            key={detailQuery.data.category.id}
            detail={detailQuery.data}
            onSave={saveCategory}
            onDelete={deleteCategoryHandler}
            onAddItem={addItem}
            onMoveItem={moveItem}
            onRemoveItem={removeItem}
          />
        ) : (
          <PanelSection title="Параметры категории">
            <p style={mutedStyle}>Выберите категорию слева.</p>
          </PanelSection>
        )}
      </div>
    </div>
  )
}

function CustomFilterEditor({
  detail,
  onSaveFilter,
  onDeleteFilter,
  onCreateOption,
  onSaveOption,
  onDeleteOption,
}: {
  detail: CustomFilterDetail
  onSaveFilter: (input: { title: string; position: number }) => Promise<void>
  onDeleteFilter: () => Promise<void>
  onCreateOption: (title: string) => Promise<void>
  onSaveOption: (optionId: string, title: string, position: number) => Promise<void>
  onDeleteOption: (optionId: string) => Promise<void>
}) {
  const [title, setTitle] = useState(detail.filter.title)
  const [position, setPosition] = useState(String(detail.filter.position))
  const [newOptionTitle, setNewOptionTitle] = useState('')
  const [optionDrafts, setOptionDrafts] = useState<Record<string, string>>(
    Object.fromEntries(detail.options.map((option) => [option.id, option.title]))
  )

  return (
    <>
      <PanelSection title="Параметры фильтра">
        <div className="grid gap-4 lg:grid-cols-2">
          <Field label="Title">
            <Input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>
          <Field label="Position">
            <Input
              type="number"
              value={position}
              onChange={(event) => setPosition(event.target.value)}
            />
          </Field>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondaryAssist" onClick={() => void onDeleteFilter()}>
            Удалить фильтр
          </Button>
          <Button
            type="button"
            onClick={() =>
              void onSaveFilter({
                title: title.trim(),
                position: Number(position || '0'),
              })
            }
          >
            Сохранить фильтр
          </Button>
        </div>
      </PanelSection>

      <PanelSection title="Опции фильтра">
        {detail.options.map((option, index) => (
          <div
            key={option.id}
            className="grid gap-3 rounded-2xl border p-4 lg:grid-cols-[1fr_auto]"
            style={panelStyle}
          >
            <div className="grid gap-2">
              <Input
                value={optionDrafts[option.id] ?? option.title}
                onChange={(event) =>
                  setOptionDrafts((current) => ({
                    ...current,
                    [option.id]: event.target.value,
                  }))
                }
              />
              <span style={mutedStyle}>position: {option.position}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === 0}
                onClick={() =>
                  void onSaveOption(
                    option.id,
                    optionDrafts[option.id] ?? option.title,
                    option.position - 1
                  )
                }
              >
                Вверх
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                disabled={index === detail.options.length - 1}
                onClick={() =>
                  void onSaveOption(
                    option.id,
                    optionDrafts[option.id] ?? option.title,
                    option.position + 1
                  )
                }
              >
                Вниз
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() =>
                  void onSaveOption(
                    option.id,
                    optionDrafts[option.id] ?? option.title,
                    option.position
                  )
                }
              >
                Сохранить
              </Button>
              <Button
                type="button"
                variant="secondaryAssist"
                onClick={() => void onDeleteOption(option.id)}
              >
                Удалить
              </Button>
            </div>
          </div>
        ))}

        <div className="grid gap-2 rounded-2xl border p-4" style={panelStyle}>
          <span style={labelStyle}>Новая опция</span>
          <Input
            value={newOptionTitle}
            onChange={(event) => setNewOptionTitle(event.target.value)}
            placeholder="Option title"
          />
          <Button
            type="button"
            onClick={() => {
              if (!newOptionTitle.trim()) {
                return
              }

              void onCreateOption(newOptionTitle.trim())
              setNewOptionTitle('')
            }}
          >
            Добавить опцию
          </Button>
        </div>
      </PanelSection>
    </>
  )
}

function CustomFiltersTab() {
  const [draftSearch, setDraftSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [requestedId, setRequestedId] = useState<string | null>(null)
  const [createTitle, setCreateTitle] = useState('')
  const [feedback, setFeedback] = useState<string | null>(null)

  const listQuery = useQuery({
    queryKey: ['admin-video-library', 'custom-filters', page, appliedSearch],
    queryFn: () => loadAdminCustomFiltersList({ page, limit: pageSize, search: appliedSearch }),
  })

  const selectedId = resolveSelectedId(listQuery.data?.data, requestedId)

  const detailQuery = useQuery({
    queryKey: ['admin-video-library', 'custom-filter-detail', selectedId],
    queryFn: () => loadAdminCustomFilterDetail(selectedId as string),
    enabled: Boolean(selectedId),
  })

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin-video-library', 'custom-filters'] })

    if (selectedId) {
      await queryClient.invalidateQueries({
        queryKey: ['admin-video-library', 'custom-filter-detail', selectedId],
      })
    }
  }

  const createFilterHandler = async () => {
    if (!createTitle.trim()) {
      return
    }

    try {
      const response = await createAdminCustomFilter({ title: createTitle.trim() })
      setCreateTitle('')
      setRequestedId(response.detail.filter.id)
      setFeedback('Фильтр создан')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const saveFilter = async (input: { title: string; position: number }) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminCustomFilter(selectedId, input)
      setFeedback('Фильтр сохранён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const createOption = async (title: string) => {
    if (!selectedId) {
      return
    }

    try {
      await createAdminCustomFilterOption({ id: selectedId, title })
      setFeedback('Опция создана')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const saveOption = async (optionId: string, title: string, position: number) => {
    if (!selectedId) {
      return
    }

    try {
      await updateAdminCustomFilterOption({
        id: selectedId,
        optionId,
        title: title.trim(),
        position,
      })
      setFeedback('Опция сохранена')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const deleteOptionHandler = async (optionId: string) => {
    if (!selectedId || !window.confirm('Удалить эту опцию?')) {
      return
    }

    try {
      await deleteAdminCustomFilterOption({ id: selectedId, optionId })
      setFeedback('Опция удалена')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  const deleteFilterHandler = async () => {
    if (!selectedId || !window.confirm('Удалить фильтр и все его опции?')) {
      return
    }

    try {
      await deleteAdminCustomFilter(selectedId)
      setRequestedId(null)
      setFeedback('Фильтр удалён')
      await refresh()
    } catch (error) {
      setFeedback(formatError(error))
    }
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
      <PanelSection title="Кастомные фильтры" description="Порядок фильтров и их options.">
        <div className="grid gap-3">
          <Input
            value={draftSearch}
            onChange={(event) => setDraftSearch(event.target.value)}
            placeholder="Поиск по title"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setAppliedSearch(draftSearch.trim())
              }}
            >
              Применить
            </Button>
            <Button
              type="button"
              variant="secondaryAssist"
              onClick={() => {
                setPage(1)
                setDraftSearch('')
                setAppliedSearch('')
              }}
            >
              Сбросить
            </Button>
          </div>
        </div>

        <div className="grid gap-2 rounded-2xl border p-4" style={panelStyle}>
          <span style={labelStyle}>Новый фильтр</span>
          <Input
            value={createTitle}
            onChange={(event) => setCreateTitle(event.target.value)}
            placeholder="Filter title"
          />
          <Button type="button" onClick={() => void createFilterHandler()}>
            Создать
          </Button>
        </div>

        {(listQuery.data?.data ?? []).map((item) => (
          <ListSelectionButton
            key={item.id}
            active={selectedId === item.id}
            onClick={() => setRequestedId(item.id)}
          >
            <div className="flex flex-col gap-1">
              <strong style={smallBodyStyle}>{item.title}</strong>
              <span style={mutedStyle}>
                position: {item.position} · options: {item.optionCount}
              </span>
            </div>
          </ListSelectionButton>
        ))}

        {listQuery.data ? (
          <PaginationControls
            page={page}
            hasMore={listQuery.data.meta.hasMore}
            total={listQuery.data.meta.total}
            limit={pageSize}
            onPrevious={() => setPage((current) => Math.max(1, current - 1))}
            onNext={() => setPage((current) => current + 1)}
            onSelectPage={setPage}
          />
        ) : null}
      </PanelSection>

      <div className="flex flex-col gap-6">
        {feedback ? (
          <FeedbackBanner message={feedback} tone={successToneFromMessage(feedback)} />
        ) : null}

        {detailQuery.data ? (
          <CustomFilterEditor
            key={detailQuery.data.filter.id}
            detail={detailQuery.data}
            onSaveFilter={saveFilter}
            onDeleteFilter={deleteFilterHandler}
            onCreateOption={createOption}
            onSaveOption={saveOption}
            onDeleteOption={deleteOptionHandler}
          />
        ) : (
          <PanelSection title="Параметры фильтра">
            <p style={mutedStyle}>Выберите фильтр слева.</p>
          </PanelSection>
        )}
      </div>
    </div>
  )
}

export function VideoLibraryAdminWorkspace({ activeTab }: WorkspaceProps) {
  if (activeTab === 'user-access') {
    return <UserAccessTab />
  }

  if (activeTab === 'bundles') {
    return <BundlesTab />
  }

  if (activeTab === 'categories') {
    return <CategoriesTab />
  }

  if (activeTab === 'custom-filters') {
    return <CustomFiltersTab />
  }

  if (activeTab === 'resources') {
    return <ResourcesTab />
  }

  if (activeTab === 'collections') {
    return <CollectionsTab />
  }

  return <VideosTab />
}
